import { useCallback } from 'react';
import { WithdrawConfirmationMethod, WithdrawSearchParamValues } from './props';
import { useCurrentModal } from '../../modals/useCurrentModal';
import { CommonModalsKey, WithdrawStep } from '../../../enums';
import { CoinType } from '../../../models';
import { Nullable } from '../../../interfaces';
import { useSearchParams } from '../../useSearchParams';
import { useNavigate } from '../../useNavigate';
import { NavigateFunction } from '@starsoft/common/interfaces/navigate-function';
import { WithdrawMethodIdType } from '@starsoft/common/enums/withdraw-method/id';

/**
 * Custom hook to manage withdraw search parameters.
 * It provides current search parameters and functions to update or delete them.
 */
export function useWithdrawSearchParams() {
  // Retrieves the current URL search parameters
  const search: URLSearchParams = useSearchParams();
  // Retrieves the current modal key, if any
  const modal: string | undefined = useCurrentModal(CommonModalsKey);
  // Provides a navigation function to update the URL
  const navigate: NavigateFunction = useNavigate();

  // Extracts the current transaction step from the search parameters or defaults to WithdrawStep.Form
  const transactionStep: WithdrawStep =
    (search.get('step') as WithdrawStep) ?? WithdrawStep.Form;
  // Extracts the coin type from the search parameters or defaults to CoinType.Fiat
  const coinType: CoinType =
    (search.get('coin-type') as CoinType) ?? CoinType.Fiat;
  // Retrieves the method ID from the search parameters
  const searchMethodId: string | null = search.get('method-id');
  // Converts the method ID to a nullable string
  const withdrawMethodId: Nullable<string> = searchMethodId
    ? (searchMethodId as unknown as string)
    : null;
  // Retrieves the coin ID from the search parameters
  const searchCoinId: string | null = search.get('coin-id');
  // Converts the coin ID to a nullable string
  const coinId: Nullable<string> = searchCoinId ? searchCoinId : null;
  // Retrieves the amount from the search parameters and converts it to a number
  const amount: Nullable<number> = Number(search.get('amount') ?? 0);
  // Retrieves the user payment details from the search parameters
  const userPaymentDetails: string = search.get('user-payment');
  // Extracts the confirmation method from the search parameters or defaults to WithdrawConfirmationMethod.Email
  const confirmationMethod =
    (search.get('confirmation-method') as WithdrawConfirmationMethod) ??
    WithdrawConfirmationMethod.Email;

  /**
   * Sets a search parameter and updates the URL.
   * @param searchParamKey - The key of the search parameter to set.
   * @param value - The value to set for the search parameter.
   * Example: handleSetWithdrawSearchParams('amount', 100);
   */
  const handleSetWithdrawSearchParams = useCallback(
    <K extends keyof WithdrawSearchParamValues>(
      searchParamKey: K,
      value: WithdrawSearchParamValues[K],
    ) => {
      if (!modal) {
        return;
      }
      search.set(searchParamKey, `${value}`);
      navigate(
        {
          search: search.toString(),
          hash: modal,
        },
        {
          preventScrollReset: true,
        },
      );
    },
    [search, modal, navigate],
  );

  /**
   * Deletes a search parameter and updates the URL.
   * @param searchParamKey - The key of the search parameter to delete.
   * Example: handleDeleteWithdrawSearchParams('amount');
   */
  const handleDeleteWithdrawSearchParams = useCallback(
    <K extends keyof WithdrawSearchParamValues>(searchParamKey: K) => {
      search.delete(searchParamKey);
      navigate(
        {
          search: search.toString(),
          hash: modal,
        },
        {
          preventScrollReset: true,
        },
      );
    },
    [search, modal, navigate],
  );

  // Returns the current search parameters and functions to modify them
  return {
    transactionStep,
    coinType,
    withdrawMethodId,
    coinId,
    amount,
    userPaymentDetails,
    confirmationMethod,
    handleSetWithdrawSearchParams,
    handleDeleteWithdrawSearchParams,
  };
}
